/**
 * Feature source for the Scale gizmo.
 *
 * On a Giro3D twin the vector layer is routed to Giro3D, so the MapLibre
 * overlay has no style layers to query and `queryRenderedFeatures` finds
 * nothing. The Vector ToolCard has already loaded the geometry for its
 * attribute table, so Scale picks from that instead. PMTiles rows arrive in
 * EPSG:3857 and have to be brought back to lon/lat first.
 */
import { bboxFromGeometry, type LonLat, type ScaleBbox } from './vectorScaleBox';

const ORIGIN_SHIFT = 20037508.342789244;

export interface ScaleSourceFeature {
  readonly id?: string | number;
  readonly properties?: Record<string, unknown>;
  readonly geometry?: unknown;
}

export function toLonLatGeometry(geometry: unknown): unknown {
  return mapPositions(geometry, toLonLat);
}

/** Normalize the ToolCard rows into lon/lat features Scale can hit-test. */
export function scaleFeaturesFrom(
  features: ReadonlyArray<ScaleSourceFeature> | undefined,
): ScaleSourceFeature[] {
  if (!features) return [];
  return features
    .filter((feature) => Boolean(feature.geometry))
    .map((feature) => ({
      id: feature.id,
      properties: feature.properties,
      geometry: toLonLatGeometry(feature.geometry),
    }));
}

/**
 * Smallest bounding box containing the point wins, so a click inside nested
 * admin polygons grabs the inner one rather than the enclosing state.
 */
export function matchScaleFeature(
  features: ReadonlyArray<ScaleSourceFeature>,
  selectedId: string | number | null | undefined,
): ScaleSourceFeature | null {
  if (selectedId == null || selectedId === '') return null;
  const wanted = String(selectedId);
  return (
    features.find((feature) => {
      if (feature.id != null && String(feature.id) === wanted) return true;
      const props = feature.properties ?? {};
      return [props.id, props.OBJECTID, props.OBJECTID_1, props.gml_id].some(
        (value) => value != null && String(value) === wanted,
      );
    }) ?? null
  );
}

export function pickFeatureAtLonLat(
  features: ReadonlyArray<ScaleSourceFeature>,
  point: LonLat,
): ScaleSourceFeature | null {
  let best: { feature: ScaleSourceFeature; area: number } | null = null;
  for (const feature of features) {
    const bbox = bboxFromGeometry(feature.geometry);
    if (
      !bbox
      || !containsPoint(bbox, point)
      || !containsGeometryPoint(feature.geometry, point)
    ) {
      continue;
    }
    const area = (bbox.maxX - bbox.minX) * (bbox.maxY - bbox.minY);
    if (!best || area < best.area) best = { feature, area };
  }
  return best?.feature ?? null;
}

function containsPoint(bbox: ScaleBbox, [lon, lat]: LonLat): boolean {
  return lon >= bbox.minX && lon <= bbox.maxX && lat >= bbox.minY && lat <= bbox.maxY;
}

function containsGeometryPoint(geometry: unknown, point: LonLat): boolean {
  if (!geometry || typeof geometry !== 'object') return false;
  const record = geometry as {
    type?: unknown;
    coordinates?: unknown;
    geometries?: unknown;
  };
  if (record.type === 'Polygon') return polygonContains(record.coordinates, point);
  if (record.type === 'MultiPolygon' && Array.isArray(record.coordinates)) {
    return record.coordinates.some((polygon) => polygonContains(polygon, point));
  }
  if (record.type === 'GeometryCollection' && Array.isArray(record.geometries)) {
    return record.geometries.some((child) => containsGeometryPoint(child, point));
  }
  // Scale is intended for polygons; retain bbox picking only for unusual data.
  return true;
}

function polygonContains(value: unknown, point: LonLat): boolean {
  if (!Array.isArray(value) || value.length === 0) return false;
  if (!ringContains(value[0], point)) return false;
  return !value.slice(1).some((hole) => ringContains(hole, point));
}

function ringContains(value: unknown, [x, y]: LonLat): boolean {
  if (!Array.isArray(value) || value.length < 3) return false;
  let inside = false;
  for (let i = 0, j = value.length - 1; i < value.length; j = i, i += 1) {
    const a = value[i];
    const b = value[j];
    if (
      !Array.isArray(a)
      || !Array.isArray(b)
      || typeof a[0] !== 'number'
      || typeof a[1] !== 'number'
      || typeof b[0] !== 'number'
      || typeof b[1] !== 'number'
    ) {
      continue;
    }
    const intersects =
      a[1] > y !== b[1] > y
      && x < ((b[0] - a[0]) * (y - a[1])) / (b[1] - a[1]) + a[0];
    if (intersects) inside = !inside;
  }
  return inside;
}

function toLonLat(x: number, y: number): LonLat {
  if (Math.abs(x) <= 180 && Math.abs(y) <= 90) return [x, y];
  const lon = (x / ORIGIN_SHIFT) * 180;
  const lat = (Math.atan(Math.sinh((y / ORIGIN_SHIFT) * Math.PI)) * 180) / Math.PI;
  return [lon, lat];
}

function mapPositions(value: unknown, map: (x: number, y: number) => LonLat): unknown {
  if (!value || typeof value !== 'object') return value;
  const record = value as { coordinates?: unknown; geometries?: unknown };
  if (Array.isArray(record.geometries)) {
    return { ...record, geometries: record.geometries.map((child) => mapPositions(child, map)) };
  }
  if (!('coordinates' in record)) return value;
  return { ...record, coordinates: mapCoords(record.coordinates, map) };
}

function mapCoords(coords: unknown, map: (x: number, y: number) => LonLat): unknown {
  if (!Array.isArray(coords) || coords.length === 0) return coords;
  if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
    return [...map(coords[0], coords[1])];
  }
  return coords.map((child) => mapCoords(child, map));
}
